/**
 * PWA installation and platform detection utilities
 */

let deferredInstallPrompt = null;

/**
 * Check if running inside the native iOS app (WKWebView)
 * WKWebView has AppleWebKit but not Safari in the user agent
 * @returns {boolean}
 */
export function isNativeApp() {
  if (window.__HANDSHAKE_NATIVE) return true;
  const ua = navigator.userAgent;
  return /iPhone|iPad|iPod/.test(ua) && /AppleWebKit/.test(ua) && !/Safari/.test(ua);
}

/**
 * Check if running on iOS
 * @returns {boolean}
 */
export function isIOS() {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
}

/**
 * Check if running on Android
 * @returns {boolean}
 */
export function isAndroid() {
  return /Android/.test(navigator.userAgent);
}

/**
 * Check if app is running in standalone mode (installed PWA)
 * @returns {boolean}
 */
export function isStandalone() {
  return window.matchMedia('(display-mode: standalone)').matches ||
         window.navigator.standalone === true ||
         document.referrer.includes('android-app://');
}

/**
 * Check if the browser supports PWA installation
 * @returns {boolean}
 */
export function canInstall() {
  return deferredInstallPrompt !== null || (isIOS() && !isStandalone());
}

/**
 * Initialize PWA install prompt listener
 * @param {Function} onCanInstall - Callback when install becomes available
 */
export function initInstallPrompt(onCanInstall) {
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    if (onCanInstall) onCanInstall();
  });

  // Also notify for iOS
  if (isIOS() && !isStandalone() && onCanInstall) {
    onCanInstall();
  }
}

/**
 * Trigger the install prompt (Android/Chrome)
 * @returns {Promise<boolean>} True if user accepted
 */
export async function promptInstall() {
  if (!deferredInstallPrompt) {
    return false;
  }

  deferredInstallPrompt.prompt();
  const { outcome } = await deferredInstallPrompt.userChoice;
  deferredInstallPrompt = null;
  return outcome === 'accepted';
}

/**
 * Register the service worker
 * @returns {Promise<ServiceWorkerRegistration|null>}
 */
export async function registerServiceWorker() {
  if ('serviceWorker' in navigator) {
    try {
      const registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', registration.scope);
      return registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      return null;
    }
  }
  return null;
}

/**
 * Create iOS install instructions modal HTML
 * @returns {string}
 */
export function createIOSInstallModal() {
  return `
    <div id="ios-install-modal" class="fixed inset-0 bg-black/50 flex items-end justify-center z-50 p-4">
      <div class="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-slide-up">
        <div class="p-6">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-lg font-bold text-slate-800">Install Handshake</h3>
            <button id="close-ios-modal" class="p-2 text-slate-400 hover:text-slate-600">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <p class="text-slate-600 text-sm mb-4">
            Add Handshake to your home screen for quick access to your verification codes, even offline.
          </p>

          <ol class="space-y-4">
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                </svg>
              </span>
              <div>
                <p class="font-medium text-slate-800">Tap the Share button</p>
                <p class="text-sm text-slate-500">At the bottom of Safari</p>
              </div>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mr-3">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                </svg>
              </span>
              <div>
                <p class="font-medium text-slate-800">Tap "Add to Home Screen"</p>
                <p class="text-sm text-slate-500">Scroll down in the share sheet</p>
              </div>
            </li>
            <li class="flex items-start">
              <span class="flex-shrink-0 w-8 h-8 bg-green-100 text-green-600 rounded-full flex items-center justify-center mr-3">
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" />
                </svg>
              </span>
              <div>
                <p class="font-medium text-slate-800">Tap "Add"</p>
                <p class="text-sm text-slate-500">Handshake will appear on your home screen</p>
              </div>
            </li>
          </ol>
        </div>

        <div class="px-6 py-4 bg-slate-50 border-t border-slate-100">
          <button id="dismiss-ios-modal" class="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-medium rounded-xl transition-colors">
            Got it
          </button>
        </div>
      </div>
    </div>
  `;
}

/**
 * Show iOS install modal
 */
export function showIOSInstallModal() {
  // Don't show if already installed or in native app
  if (isStandalone() || isNativeApp()) return;

  // Check if user dismissed before
  if (localStorage.getItem('handshake_ios_install_dismissed')) return;

  const modal = document.createElement('div');
  modal.innerHTML = createIOSInstallModal();
  document.body.appendChild(modal.firstElementChild);

  // Add animation styles if not present
  if (!document.getElementById('pwa-styles')) {
    const style = document.createElement('style');
    style.id = 'pwa-styles';
    style.textContent = `
      @keyframes slide-up {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      .animate-slide-up {
        animation: slide-up 0.3s ease-out;
      }
    `;
    document.head.appendChild(style);
  }

  // Attach close handlers
  const closeModal = () => {
    const modalEl = document.getElementById('ios-install-modal');
    if (modalEl) modalEl.remove();
  };

  const dismissModal = () => {
    localStorage.setItem('handshake_ios_install_dismissed', 'true');
    closeModal();
  };

  document.getElementById('close-ios-modal')?.addEventListener('click', closeModal);
  document.getElementById('dismiss-ios-modal')?.addEventListener('click', dismissModal);
  document.getElementById('ios-install-modal')?.addEventListener('click', (e) => {
    if (e.target.id === 'ios-install-modal') closeModal();
  });
}
